import React from "react";
import type { BankType } from "../../../../domain/types/company-bank-transfer";
import { Landmark, X } from "lucide-react";

export const MAX_AUTO_CONFIRM_ACCOUNTS = 10;

declare global {
  interface Window {
    TossPayments?: (clientKey: string) => {
      payment: (options: { customerKey: string }) => {
        requestBillingAuth: (options: {
          method: "CARD";
          successUrl: string;
          failUrl: string;
          customerName?: string;
        }) => Promise<void>;
      };
    };
  }
}

export const BANKS: Array<{ value: BankType; label: string }> = [
  { value: "KB_KOOKMIN", label: "KB국민은행" },
  { value: "SHINHAN", label: "신한은행" },
  { value: "WOORI", label: "우리은행" },
  { value: "HANA", label: "하나은행" },
  { value: "NH_NONGHYUP", label: "NH농협은행" },
  { value: "IBK", label: "IBK기업은행" },
  { value: "KDB", label: "KDB산업은행" },
  { value: "SC", label: "SC제일은행" },
  { value: "CITI", label: "한국씨티은행" },
  { value: "KYONGNAM", label: "경남은행" },
  { value: "GWANGJU", label: "광주은행" },
  { value: "DAEGU", label: "iM뱅크(대구)" },
  { value: "BUSAN", label: "부산은행" },
  { value: "JEONBUK", label: "전북은행" },
  { value: "JEJU", label: "제주은행" },
  { value: "SH_SUHYUP", label: "Sh수협은행" },
  { value: "K_BANK", label: "케이뱅크" },
  { value: "SAEMAUL", label: "새마을금고" },
  { value: "SHINHYUP", label: "신협" },
  { value: "POST_OFFICE", label: "우체국" },
];

export const BANK_LOGO: Partial<Record<BankType, string>> = {
  KDB: "002",
  IBK: "003",
  KB_KOOKMIN: "004",
  SH_SUHYUP: "007",
  NH_NONGHYUP: "011",
  WOORI: "020",
  SC: "023",
  CITI: "027",
  DAEGU: "031",
  BUSAN: "032",
  GWANGJU: "034",
  JEJU: "035",
  JEONBUK: "037",
  KYONGNAM: "039",
  SAEMAUL: "045",
  SHINHYUP: "048",
  POST_OFFICE: "071",
  HANA: "081",
  SHINHAN: "088",
  K_BANK: "089",
};

export const BankMark = ({ bank, small = false }: { bank: BankType; small?: boolean }) => {
  const bankName = BANKS.find((item) => item.value === bank)?.label ?? "은행";
  const logoCode = BANK_LOGO[bank];
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center border border-[#e5e8ee] bg-white shadow-sm ${
        small ? "h-9 w-9 rounded-lg" : "h-12 w-12 rounded-[10px]"
      }`}
      title={bankName}
    >
      {logoCode ? (
        <img
          src={`/assets/bank-logos/${logoCode}.svg`}
          alt={`${bankName} 로고`}
          className={small ? "h-6 w-6 object-contain" : "h-8 w-8 object-contain"}
        />
      ) : (
        <Landmark
          aria-label={`${bankName} 로고`}
          className="text-[#4285F4]"
          size={small ? 20 : 26}
        />
      )}
    </span>
  );
};

export const requiresWeb = new Set<BankType>([
  "KB_KOOKMIN",
  "SHINHAN",
  "DAEGU",
  "SHINHYUP",
  "K_BANK",
]);
export const requiresWebPassword = new Set<BankType>([
  "SHINHAN",
  "DAEGU",
  "SHINHYUP",
  "K_BANK",
]);
export const requiresIdentity = new Set<BankType>([
  "WOORI",
  "HANA",
  "NH_NONGHYUP",
  "IBK",
  "KDB",
  "SC",
  "CITI",
  "KYONGNAM",
  "GWANGJU",
  "BUSAN",
  "JEONBUK",
  "JEJU",
  "SH_SUHYUP",
  "SAEMAUL",
  "POST_OFFICE",
]);



/** 은행 코드 → 사람이 읽는 이름. 모르는 코드는 코드 그대로 보여준다 */
export const bankLabel = (bank: BankType) =>
  BANKS.find((b) => b.value === bank)?.label ?? bank;

export const statusCopy = {
  ACTIVATING: ["등록 중", "bg-blue-50 text-blue-700"],
  ACTIVE: ["이용 중", "bg-emerald-50 text-emerald-700"],
  PAST_DUE: ["결제 실패", "bg-amber-50 text-amber-700"],
  CANCEL_REQUESTED: ["해지 예정", "bg-slate-100 text-slate-600"],
  ENDED: ["종료", "bg-rose-50 text-rose-700"],
} as const;

/**
 * 결제창을 사용자가 그냥 닫은 경우.
 *
 * 결제사 SDK 는 이때도 예외를 던진다. 그대로 잡아 배너에 올리면 사장님이 스스로 닫아놓고
 * **"취소되었습니다."라는 빨간 오류**를 보게 된다. 취소는 실패가 아니라 선택이라 조용히 넘긴다.
 */
export const isUserCanceled = (e: unknown) =>
  typeof e === "object" &&
  e !== null &&
  ["USER_CANCEL", "PAY_PROCESS_CANCELED"].includes(
    (e as { code?: string }).code ?? "",
  );

export const loadTossSdk = () =>
  new Promise<void>((resolve, reject) => {
    if (window.TossPayments) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://js.tosspayments.com/v2/standard"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v2/standard";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("결제창을 열지 못했습니다. 잠시 후 다시 시도해 주세요."));
    document.head.appendChild(script);
  });


export const inputClass =
  "h-12 w-full rounded-lg border border-[#dce0e7] bg-white px-3.5 text-sm text-[#303644] outline-none transition placeholder:text-[#b0b6c1] focus:border-[#4285F4] focus:ring-2 focus:ring-[#4285F4]/10";

export const StepTitle = ({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) => (
  <div className="flex gap-3">
    <span className="pt-1 text-xs font-bold text-[#4285F4]">{number}</span>
    <div>
      <h2 className="text-xl font-bold tracking-[-0.5px] text-[#252b38]">{title}</h2>
      <p className="mt-1.5 text-xs leading-5 text-[#818999]">{description}</p>
    </div>
  </div>
);

export const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-bold text-[#555e6f]">
      {label} <span className="text-rose-500">*</span>
    </span>
    {children}
  </label>
);


export const ModalShell = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <div
    className="fixed inset-0 z-[999999] flex items-center justify-center bg-[#0B1220]/50 p-4"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}
  >
    {children}
  </div>
);
